import React, { useMemo, memo } from "react";
import type { BiddingInfo } from "../../../types";
import { ListingScheduleDetails } from "./ListingScheduleDetails";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import { Heart } from "lucide-react";
import Big from "big.js";
import { useGetFavoritesQuery, useToggleFavoriteMutation } from "../../../hooks/useBiddingItemsQuery";
import { useAuth } from "../../../contexts/AuthContext";

// 一覧行用Props
interface ListingScheduleItemRowProps {
  item: BiddingInfo;
  isSelected: boolean;
  onToggleSelect: () => void;
}

// # 一覧の各行を表すコンポーネント ---
export const ListingScheduleItemRow: React.FC<ListingScheduleItemRowProps> = ({
  item,
  isSelected,
  onToggleSelect,
}) => {
  //----------------
  // Hooks / Data取得
  //----------------
  // 1. 現在のログインユーザー情報を取得
  const { currentUser } = useAuth();
  const loginId = currentUser?.loginId || "";

  // 2. お気に入りリスト（名簿）をサーバーから取得
  const { data: favorites } = useGetFavoritesQuery(loginId);
  
  // 3. お気に入り更新用の通信機能を準備
  const { mutate, isPending } = useToggleFavoriteMutation(loginId);

  //----------------
  // 判定ロジック
  //----------------
  // サーバーから届いた名簿（favorites）の中に、この行の「ID」と「枝番」があるか判定
  const isFavorite = useMemo(() => {
    if (!favorites) return false;
    return favorites.some(
      (f) => f.itemId === item.itemId && f.branchNumber === item.branchNumber
    );
  }, [favorites, item.itemId, item.branchNumber]);

  // ツールチップ
  const { tooltipProps, TooltipElement } = useTruncatedTooltip();

  //----------------
  // Memo定義（計算・表示制御）
  //----------------
  const displayInfo = useMemo(() => {
    try {
      // 1. 各値を Big オブジェクトに変換
      const c1 = new Big(item.capacity1 || 0);
      const c2 = new Big(item.capacity2 || 0);
      const q1 = new Big(item.quantity1 || 0);
      const q2 = new Big(item.quantity2 || 0);
      const bQ1 = new Big(item.bidQuantity1 || 0);
      const bQ2 = new Big(item.bidQuantity2 || 0);

      // 2. 計算（総量と入札量）
      const total = c1.times(q1).plus(c2.times(q2));
      const bid = c1.times(bQ1).plus(c2.times(bQ2));

      return {
        totalCapacity: total.toNumber(),
        bidCapacity: bid.toNumber(),
      };
    } catch (e) {
      return { totalCapacity: 0, bidCapacity: 0 };
    }
  }, [
    item.capacity1,
    item.quantity1,
    item.capacity2,
    item.quantity2,
    item.bidQuantity1,
    item.bidQuantity2,
  ]);

  // 生産者名を表示してよいかの判定
  const canSeeFarmerName = useMemo(() => {
    return !item.isFarmerHidden;
  }, [item.isFarmerHidden]);

  //----------------
  // イベントハンドラ
  //----------------
  // お気に入りボタンクリック時の処理
  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation(); // 行の開閉イベントが動かないようにする
    if (isPending || !loginId) return; // 通信中や未ログイン時は無効

    // お気に入り更新APIを呼び出す
    mutate({ 
      itemId: item.itemId, 
      branchNumber: item.branchNumber 
    });
  };

  const rowClasses = "hover:bg-slate-50";

  //----------------
  // レンダリング処理
  //----------------
  return (
    <>
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white ${
          isSelected ? "ring-2 ring-indigo-500 rounded-lg" : ""
        }`}
      >
        {/* --- クリック可能な行ヘッダー --- */}
        <div
          className={`lg:p-4 ${rowClasses} transition-colors cursor-pointer relative`}
          onClick={onToggleSelect}
        >
          <div className="flex flex-col sm:flex-row sm:items-start">
            {/* --- 表示内容エリア --- */}
            <div className="flex-1 grid grid-cols-6 lg:grid-cols-12 gap-x-2 gap-y-1 sm:gap-x-4 items-baseline lg:items-start pr-8 sm:pr-0">
              
              {/* --- お気に入りボタン --- */}
              <div className="col-span-1 order-1 lg:order-none lg:col-span-1 min-w-[3rem] flex flex-col items-center">
                <button
                  onClick={handleToggleFavorite}
                  disabled={isPending}
                  className="translate-y-2 lg:translate-y-2 p-1 -ml-2 lg:-ml-6 rounded-full hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-50"
                  type="button"
                >
                  <Heart
                    className={`w-5 lg:w-8 h-5 lg:h-8 transition-all ${
                      isFavorite 
                        ? "fill-red-400 text-red-400"
                        : "text-slate-400"
                    }`}
                  />
                </button>
              </div>

              {/* 入札番号 */}
              <div className="col-span-1 order-2 lg:order-none lg:col-span-1 min-w-[3rem]">
                <p className="hidden lg:block text-base text-slate-500 lg:-ml-4">
                  入札番号
                </p>
                <p className="text-base font-bold text-left lg:-ml-4">
                  {item.biddingNumber}
                </p>
              </div>

              {/* 売手 */}
              <div className="col-span-4 order-3 lg:order-none lg:col-span-2 flex flex-col items-start lg:mt-0">
                <p className="hidden lg:block text-base text-slate-500">売手</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg lg:font-bold truncate max-w-full lg:w-fit pointer-events-auto"
                >
                  {item.sellerName}
                </p>
              </div>

              {/* 生産者 */}
              <div className="col-span-4 order-4 lg:order-none lg:col-span-2 flex flex-col items-start lg:mt-0">
                <p className="hidden lg:block text-base text-slate-500">生産者</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg lg:font-bold truncate max-w-full lg:w-fit pl-2 lg:pl-0 pointer-events-auto"
                >
                  {canSeeFarmerName ? (
                    item.farmerName
                  ) : (
                    <span className="text-slate-400 font-normal italic">-</span>
                  )}
                </p>
              </div>

              {/* 荷印 */}
              <div className="col-span-2 order-5 lg:order-none lg:col-span-2 text-left lg:pl-4">
                <p className="hidden lg:block text-base text-slate-500">荷印</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg text-left lg:font-bold truncate ml-2.5 lg:ml-0 pointer-events-auto"
                >
                  {item.brand}
                </p>
              </div>

              {/* PCのみ表示 (茶期・茶種・品種) */}
              <div className="hidden lg:block lg:col-span-1">
                <p className="text-base text-slate-500">茶期</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.teaSeasonName}
                </p>
              </div>
              <div className="hidden lg:block lg:col-span-1">
                <p className="text-base text-slate-500">茶種</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.teaTypeName}
                </p>
              </div>
              <div className="hidden lg:block lg:col-span-1">
                <p className="text-base text-slate-500">品種名</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.varietyName}
                </p>
              </div>

              {/* 総量 */}
              <div className="hidden lg:block col-span-1 lg:order-none lg:col-span-1 lg:pl-2 text-right lg:text-left">
                <p className="hidden lg:block text-base text-slate-500 whitespace-nowrap">
                  総量(kg)
                </p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg lg:font-bold truncate"
                >
                  {displayInfo.totalCapacity}
                  <span className="lg:hidden text-base lg:text-sm font-normal ml-0.5 text-slate-600">
                    kg
                  </span>
                </p>
              </div>
            </div>

            {/* モバイル用簡易表示 */}
            <div className="md:hidden py-2 text-base flex flex-wrap gap-x-3 pointer-events-none justify-left border-t border-slate-100 ml-2.5">
              <span>{item.teaSeasonName}</span>
              <span>/</span>
              <span>{item.teaTypeName}</span>
              <span>/</span>
              <span>{item.varietyName}</span>
              <span>/</span>
              <span>{displayInfo.totalCapacity}kg</span>
            </div>

            {/* モバイル用 展開アイコン */}
            <div className="absolute top-4 right-4 sm:hidden pointer-events-none">
              <svg
                className={`w-6 h-6 text-slate-400 transition-transform ${
                  isSelected ? "rotate-180" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* --- 詳細情報エリア --- */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isSelected ? "max-h-[1000px]" : "max-h-0"
          }`}
        >
          {isSelected && (
            <div className="border-t border-slate-200">
              <ListingScheduleDetails item={item} displayInfo={displayInfo} />
            </div>
          )}
        </div>
      </div>
      {TooltipElement}
    </>
  );
};

// React.memo で描画コストを最適化
export default memo(ListingScheduleItemRow);